import { Box } from '@mui/material';
import { useEffect, useRef, useState, type FC, type MouseEvent } from 'react';

interface PreviewResizeHandleProps {
    currentWidth: number;
    minWidth: number;
    getMaxWidth: () => number;
    onWidthChange: (width: number) => void;
}

export const PreviewResizeHandle: FC<PreviewResizeHandleProps> = ({
    currentWidth,
    minWidth,
    getMaxWidth,
    onWidthChange,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ clientX: 0, width: 0 });

    useEffect(() => {
        if (!isDragging) {
            return undefined;
        }

        const handleMouseMove = (event: globalThis.MouseEvent) => {
            const delta = dragStartRef.current.clientX - event.clientX;
            const nextWidth = Math.min(
                getMaxWidth(),
                Math.max(minWidth, dragStartRef.current.width + delta)
            );
            onWidthChange(nextWidth);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [getMaxWidth, isDragging, minWidth, onWidthChange]);

    const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        dragStartRef.current = { clientX: event.clientX, width: currentWidth };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        setIsDragging(true);
    };

    return (
        <Box
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize live preview"
            aria-valuenow={Math.round(currentWidth)}
            aria-valuemin={minWidth}
            onMouseDown={handleMouseDown}
            sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: -3,
                width: 8,
                cursor: 'col-resize',
                zIndex: 2,
                bgcolor: isDragging ? 'rgba(131, 83, 226, 0.45)' : 'transparent',
                '&:hover': {
                    bgcolor: isDragging ? 'rgba(131, 83, 226, 0.45)' : 'rgba(131, 83, 226, 0.28)',
                },
            }}
        />
    );
};
